import { create } from 'zustand'
import { api, refreshSession, setAccessToken, setSignedOutHandler } from '../lib/api'

export type CurrentUser = {
  id: string
  name: string
  email: string
  role: string
  locationId: string | null
  locationName: string | null
  permissions: string[]
  /** Whether they have set an approval PIN. */
  hasPin?: boolean
  /** Their role's till discount limit; null or absent is unlimited. */
  maxDiscountPercent?: number | null
  /** They sign in with an authenticator code as well (D-127). */
  twoFactorEnabled?: boolean
  /** Their role requires it: until it is on, setting it up is all there is. */
  twoFactorRequired?: boolean
}

type AuthResponse = {
  accessToken: string
  accessTokenExpiresAt: string
  mustChangePassword: boolean
  user: CurrentUser
}

type SessionState = {
  user: CurrentUser | null
  mustChangePassword: boolean
  /** True until the initial refresh attempt settles, so routes do not flash. */
  loading: boolean
  permissions: Set<string>

  /** Resolves with a challenge when a code from the authenticator app is needed next (D-127). */
  signIn: (email: string, password: string) => Promise<{ challenge: string } | null>
  completeTwoFactor: (challenge: string, answer: { code?: string; recoveryCode?: string }) => Promise<void>
  /** Two-factor was just turned on or off: the session says so without a round trip. */
  setTwoFactorEnabled: (enabled: boolean) => void
  signOut: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  restore: () => Promise<void>
  can: (permission: string) => boolean
}

export const useSession = create<SessionState>((set, get) => {
  /** A session begins: the token in memory, the user and what they may do. */
  const start = (result: AuthResponse) => {
    setAccessToken(result.accessToken)
    set({
      user: result.user,
      mustChangePassword: result.mustChangePassword,
      permissions: new Set(result.user.permissions),
      loading: false,
    })
  }

  return {
  user: null,
  mustChangePassword: false,
  loading: true,
  permissions: new Set<string>(),

  signIn: async (email, password) => {
    const result = await api<AuthResponse | { twoFactorRequired: true; challenge: string }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      anonymous: true,
    })

    if ('challenge' in result) return { challenge: result.challenge }
    start(result)
    return null
  },

  completeTwoFactor: async (challenge, answer) => {
    start(await api<AuthResponse>('/api/auth/login/two-factor', {
      method: 'POST',
      body: { challenge, ...answer },
      anonymous: true,
    }))
  },

  setTwoFactorEnabled: (enabled) => {
    const user = get().user
    if (user) set({ user: { ...user, twoFactorEnabled: enabled } })
  },

  signOut: async () => {
    try {
      // Empty body: the refresh token is in the cookie. Best-effort — if the
      // call fails the local session still ends, because leaving someone
      // apparently signed in on a shared POS terminal is the worse outcome.
      await api<void>('/api/auth/logout', { method: 'POST', body: {} })
    } catch {
      // Deliberately swallowed; see above.
    }

    setAccessToken(null)
    set({ user: null, mustChangePassword: false, permissions: new Set() })
  },

  changePassword: async (currentPassword, newPassword) => {
    await api<void>('/api/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    })

    // The server revokes every session on a password change, this one included,
    // so there is no valid token left to carry on with.
    setAccessToken(null)
    set({ user: null, mustChangePassword: false, permissions: new Set() })
  },

  /**
   * Rebuilds the session on a page load.
   *
   * The access token lived only in memory, so a reload has none — but the refresh
   * cookie survives, and exchanging it is what makes the session feel persistent
   * without ever putting a long-lived credential where a script can read it.
   */
  restore: async () => {
    const recovered = await refreshSession()

    if (!recovered) {
      set({ user: null, loading: false, permissions: new Set() })
      return
    }

    try {
      const user = await api<CurrentUser>('/api/auth/me')
      set({
        user,
        permissions: new Set(user.permissions),
        loading: false,
      })
    } catch {
      set({ user: null, loading: false, permissions: new Set() })
    }
  },

  /**
   * Whether to show an affordance.
   *
   * A courtesy, never a control. §12 requires the policy handler on every
   * endpoint, and that is what actually refuses a request — this only avoids
   * presenting a cashier with buttons that would turn them away.
   */
  can: (permission) => get().permissions.has(permission),
  }
})

// Wired once: any request that cannot recover its session clears local state, so
// the router sends the user to the sign-in page instead of leaving a shell that
// silently fails every query.
setSignedOutHandler(() => {
  useSession.setState({ user: null, mustChangePassword: false, permissions: new Set() })
})
